/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import FileSystemPathFactory from '../file/system/file-system-path-factory';
import FileSystemType from '../file/system/file-system-type';
import LogicalUnit from '../logical-unit/logical-unit';

/**
 * @import { LogicalNameToFileSystemUnitMap, LogicalUnitMapper } from '.'
 * @import { FileSystemManager } from '../file/system'
 */

/**
 * Funkce generující názvy pro logické jednotky.
 * @callback LogicalUnitNameGenerator
 * @returns {string} Název logické jednotky.
 */

// TODO: Popisek třídy je špatně.
/**
 * Resolver pro interní cestu.
 * @implements {LogicalUnitMapper}
 */
export default class SimpleLogicalUnitMapper {
  // TODO: Refaktorovat logicalUnitNameGenerator na defaultní core implementaci a řešit změnu jen pro testy, místo duplikované platformově specifické DI.
  /**
   * Vytvoří instanci.
   * @param {FileSystemManager} fileSystemManager Správce souborového systému.
   * @param {LogicalUnitNameGenerator} logicalUnitNameGenerator Generator názvů pro logické jednotky
   */
  constructor(fileSystemManager, logicalUnitNameGenerator) {
    this.fileSystemManager = fileSystemManager;
    this.logicalUnitNameGenerator = logicalUnitNameGenerator;
    // TODO: Předělat na konstantu na úrovni souboru.
    this.externalDeviceIdFilename = 'player-lighter-external';

    // TODO: Nějaké dynamická aktualizace mapperu připojených jednotek,
    // aby bylo možné je přidávat i v průběhu.
    // TODO: Refaktorovat totálně podivné asynchronní ukládání a in-place měnění hodnoty v Promise!
    /** @type {Promise<LogicalNameToFileSystemUnitMap>} */
    this.logicalNameToFileSystemUnitMapPromise = this.getUnitsMapping();
  }

  /**
   * Získá logické názvy pro všechny dostupné jednotky, které jsou řádně oštítkované.
   * @private
   * @returns {Promise<LogicalNameToFileSystemUnitMap>} Mapa mezi logickým název a fyzickou jednotkou.
   * Klíčem je LogicalUnit.getName() a hodnotou je FileSystemUnit.
   */
  getUnitsMapping() {
    return new Promise((resolve) =>
      resolve(
        this.fileSystemManager.getAllUnits().then((fileSystemUnits) =>
          fileSystemUnits.reduce(
            (acc, fileSystemUnit) =>
              acc.then(async (logicalToPhysicalMap) => {
                // Interní jednotky mají pro zpětnou kompatibilitu logické jméno stejné jako fyzické.
                let logicalName = fileSystemUnit.getName();
                if (fileSystemUnit.getType() !== FileSystemType.Internal) {
                  try {
                    const externalDeviceIdPath =
                      FileSystemPathFactory.fromValue(
                        `/${fileSystemUnit.getName()}/${
                          this.externalDeviceIdFilename
                        }`
                      );
                    if (
                      !(await this.fileSystemManager.hasFile(
                        externalDeviceIdPath
                      ))
                    ) {
                      return logicalToPhysicalMap;
                    }

                    // TODO: Podivný název, přejmenovat.
                    // TODO: Co když někdo dá \n a jiné všivé znaky do souboru.
                    const externalDeviceIdContent =
                      await this.fileSystemManager.readFile(
                        externalDeviceIdPath
                      );
                    // Externí zařízení není oštítkováno
                    if (!externalDeviceIdContent) {
                      return logicalToPhysicalMap;
                    }
                    logicalName = externalDeviceIdContent;
                  } catch (err) {
                    return logicalToPhysicalMap;
                  }
                }

                logicalToPhysicalMap[logicalName] = fileSystemUnit;
                return logicalToPhysicalMap;
              }),
            Promise.resolve({})
          )
        )
      )
    );
  }

  /** @type {LogicalUnitMapper['getAvailableLogicalUnits']} */
  getAvailableLogicalUnits() {
    return this.logicalNameToFileSystemUnitMapPromise;
  }

  /** @type {LogicalUnitMapper['refresh']} */
  refresh() {
    this.logicalNameToFileSystemUnitMapPromise = this.getUnitsMapping();
  }

  // TODO: Přejmenovat na toLogicalUnit?
  // TODO: Dokumentovat chování při očekávaných chybách jako nepřítomnost fyzické jednotky.
  /** @type {LogicalUnitMapper['toLogical']} */
  toLogical(fileSystemUnit) {
    return new Promise((resolve) =>
      resolve(
        this.logicalNameToFileSystemUnitMapPromise.then(
          async (logicalNameToFileSystemUnitMap) => {
            const fileSystemUnitName = fileSystemUnit.getName();
            // Pokud ještě nebyla externí jednotka namapovaná
            // TODO: Neoptimální O(n) hledání - nahradit za this.PhysicalToLogicalMap.
            let logicalName = Object.keys(logicalNameToFileSystemUnitMap).find(
              (candidateLogicalName) =>
                logicalNameToFileSystemUnitMap[
                  candidateLogicalName
                ].getName() === fileSystemUnitName
            );
            if (!logicalName) {
              // Interní jednotky mají pro zpětnou kompatibilitu logické jméno stejné jako fyzické.
              logicalName =
                /** @type {string} TODO #555 */
                (fileSystemUnit.getName());
              if (fileSystemUnit.getType() !== FileSystemType.Internal) {
                logicalName = this.logicalUnitNameGenerator();
                const externalDeviceIdPath = FileSystemPathFactory.fromValue(
                  `/${fileSystemUnit.getName()}/${
                    this.externalDeviceIdFilename
                  }`
                );
                // Oštítkování externího zařízení
                await this.fileSystemManager.writeFile(
                  externalDeviceIdPath,
                  logicalName
                );
              }

              logicalNameToFileSystemUnitMap[logicalName] = fileSystemUnit;
            }
            // TODO: Není optimální, stále alokuje.
            return new LogicalUnit(logicalName);
          }
        )
      )
    );
  }

  // TODO: Dokumentovat chování při očekávaných chybách jako nepřítomnost fyzické jednotky.
  /** @type {LogicalUnitMapper['toFileSystemUnit']} */
  toFileSystemUnit(logicalUnit) {
    return new Promise((resolve) =>
      resolve(
        this.logicalNameToFileSystemUnitMapPromise.then(
          (logicalToPhysicalMap) => {
            const logicalName = logicalUnit.getName();
            const fileSystemUnit = logicalToPhysicalMap[logicalName];
            if (!fileSystemUnit) {
              throw new Error(
                `unable to resolve logical unit "${logicalName}"`
              );
            }
            return fileSystemUnit;
          }
        )
      )
    );
  }
}
